import { useState, useRef, useEffect, Dispatch, SetStateAction } from 'react'
import styles from '../styles.module.css'
import PrimaryButton from '../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../global/components/buttons/secondary-button/SecondaryButton'
import { CustomerInformation } from '../../../schedule/components/schedule-modal/components/customer-information/CustomerInformation'
import { TireSizeQuantity } from '../../../schedule/components/schedule-modal/components/choose-service/TireSizeQuantity'
import { AppointmentSlots } from '../../../schedule/components/schedule-modal/components/appointment-slot/AppointmentSlots'
import { PaymentInformation } from '../../../schedule/components/schedule-modal/components/payment-information/PaymentInformation'
import { ConfirmAppointment } from '../../../schedule/components/schedule-modal/components/confirm-appointment/ConfirmAppointment'
import ConfirmationScreen from '../../../schedule/components/schedule-modal/components/appointment-is-confirmed/ConfirmationScreen'
import ToastNotification, {
  IAlertStatusProps,
} from '../../../global/components/toast-alert/ToastNotification'
import logo from '../../../assets/images/tire-rack-dot-com-logo-285x65.png'
import { ISchedulerStateProps } from '../../../schedule/components/schedule-modal/components/container-modal/initialData'
import ServiceDropDown from '../../../global/components/service-drop-down/ServiceDropDown'
import {
  MODES,
  SCHEDULING_KEYS,
  TITLES,
} from '../../../global/constants/scheduler'
import { RESCHEDULE_REASONS } from '../../../global/constants/orders'
import ComplianceLinks from '../../../global/components/compliance-links/ComplianceLinks'
import LogRocket from 'logrocket'
import { SUPPORT_PHONE_NUMBER } from '../../../global/notifications'
import {
  customerRescheduleFlow,
  customerSchedulerFlow,
} from '../../../schedule/components/schedule-modal/components/container-modal/scheduleRoutes'

interface ICustomerSchedulerProps {
  schedulerState: ISchedulerStateProps
  setSchedulerState: Dispatch<SetStateAction<ISchedulerStateProps>>
  mode: MODES
}
function Logo() {
  return (
    <img
      src={logo}
      alt='logo'
      className={styles.logo}
    />
  )
}
export default function CustomerScheduler(props: ICustomerSchedulerProps) {
  const { schedulerState, setSchedulerState, mode } = props
  const [alertStatus, setAlertStatus] = useState<IAlertStatusProps>({
    isOpen: false,
    message: `There has been an error, please try again. If problem persists, contact customer support at ${SUPPORT_PHONE_NUMBER}`,
    severity: 'error',
  })
  const toggleAlert = (open: boolean) => {
    setAlertStatus({ ...alertStatus, isOpen: open })
  }
  const [currentSchedulingPage, setCurrentSchedulingPage] =
    useState<SCHEDULING_KEYS>(
      isModeCustomerReschedule()
        ? SCHEDULING_KEYS.appointmentSlot
        : SCHEDULING_KEYS.customerInformation,
    )
  const currentFlow = isModeCustomerReschedule()
    ? customerRescheduleFlow
    : customerSchedulerFlow

  const [nextDisabled, setNextDisabled] = useState(true)

  const ref = useRef<any>(null)

  function forwardClick() {
    if (ref.current !== null) {
      ref.current.handleNextClick((forward: boolean) => {
        if (forward) {
          const nextSchedulingPage = currentFlow[currentSchedulingPage]
          setCurrentSchedulingPage(nextSchedulingPage)
        }
      })
    }
  }

  function backClick() {
    const reversedFlow = Object.fromEntries(
      Object.entries(currentFlow).map(([key, value]) => [value, key]),
    )
    const nextSchedulingPage = reversedFlow[currentSchedulingPage]
    setCurrentSchedulingPage(nextSchedulingPage as SCHEDULING_KEYS)
  }

  function isModeCustomerReschedule() {
    return mode === MODES.CUSTOMER_RESCHEDULE
  }

  useEffect(() => {
    const shouldDisplayAppointmentConfirmation =
      schedulerState &&
      schedulerState.status === 'scheduled' &&
      mode !== MODES.CUSTOMER_RESCHEDULE
    if (shouldDisplayAppointmentConfirmation) {
      setCurrentSchedulingPage(SCHEDULING_KEYS.appointmentConfirmation)
    }
  }, [schedulerState])

  useEffect(() => {
    const { firstName, lastName, email } = schedulerState.customerInformation
    const customerName = `${firstName} ${lastName}`

    LogRocket.identify(schedulerState.orderId, {
      name: customerName,
      email,
      role: 'customer', // not a formal role in the system but useful for LogRocket
    })
  }, [schedulerState])

  function renderComponent() {
    switch (currentSchedulingPage) {
      case SCHEDULING_KEYS.chooseAService:
        return (
          <TireSizeQuantity
            schedulerState={schedulerState}
            ref={ref}
            setSchedulerState={setSchedulerState}
            setDisabled={setNextDisabled}
            mode={mode}
            toggleToastError={toggleAlert}
          />
        )
      case SCHEDULING_KEYS.appointmentSlot:
        return (
          <AppointmentSlots
            schedulerState={schedulerState}
            ref={ref}
            setSchedulerState={setSchedulerState}
            setDisabled={setNextDisabled}
            rescheduleReason={
              isModeCustomerReschedule()
                ? RESCHEDULE_REASONS.customer_requested
                : undefined
            }
            mode={mode}
            toggleToastError={toggleAlert}
          />
        )
      case SCHEDULING_KEYS.paymentInformation:
        return (
          <PaymentInformation
            schedulerState={schedulerState}
            ref={ref}
            setSchedulerState={setSchedulerState}
            setDisabled={setNextDisabled}
            mode={mode}
            toggleToastError={toggleAlert}
          />
        )
      case SCHEDULING_KEYS.confirmAppointment:
        return (
          <ConfirmAppointment
            schedulerState={schedulerState}
            setSchedulerState={setSchedulerState}
            setCurrentSchedulingPage={setCurrentSchedulingPage}
            setDisabled={setNextDisabled}
            ref={ref}
            mode={mode}
            toggleToastError={toggleAlert}
          />
        )
      case SCHEDULING_KEYS.appointmentConfirmation:
        return (
          <ConfirmationScreen
            schedulerState={schedulerState}
            setSchedulerState={setSchedulerState}
            setDisabled={setNextDisabled}
            mode={mode}
          />
        )
      case SCHEDULING_KEYS.customerInformation:
      default:
        return (
          <CustomerInformation
            schedulerState={schedulerState}
            setSchedulerState={setSchedulerState}
            ref={ref}
            setDisabled={setNextDisabled}
            mode={mode}
            toggleToastError={toggleAlert}
          />
        )
    }
  }

  function renderButtons(): JSX.Element[] {
    let buttons: JSX.Element[] = []

    // If the conditions to display the 'confirm' button have changed,
    // renderComplianceLinks also needs to be updated
    if (!isModeCustomerReschedule()) {
      if (currentSchedulingPage !== SCHEDULING_KEYS.appointmentConfirmation) {
        buttons.push(
          <SecondaryButton
            disabled={
              currentSchedulingPage === SCHEDULING_KEYS.customerInformation
            }
            buttonName='Back'
            onClick={backClick}
          />,
        )
        buttons.push(
          <PrimaryButton
            disabled={nextDisabled}
            buttonName={
              currentSchedulingPage === SCHEDULING_KEYS.confirmAppointment
                ? 'Confirm'
                : 'Next'
            }
            onClick={forwardClick}
          />,
        )
      }
    } else {
      if (currentSchedulingPage === SCHEDULING_KEYS.appointmentConfirmation) {
        buttons.push(
          <SecondaryButton
            buttonName='Reschedule'
            onClick={backClick}
          />,
        )
      }
      if (currentSchedulingPage === SCHEDULING_KEYS.appointmentSlot) {
        // If the conditions to display this button have changed,
        // renderComplianceLinks also needs to be updated
        buttons.push(
          <PrimaryButton
            disabled={nextDisabled}
            buttonName='Confirm'
            onClick={forwardClick}
          />,
        )
      }
    }

    // this prevents the "missing keys" error in the console
    const buttonsWithKeys = buttons.map((button, index) => {
      return { ...button, key: index.toString() }
    })

    return buttonsWithKeys
  }

  function renderComplianceLinks(): JSX.Element {
    const complianceLinks = <ComplianceLinks />

    // If the conditions to display the "confirm" button have changed, this condition also needs updated
    const isConfirmButtonDisplayed = isModeCustomerReschedule()
      ? currentSchedulingPage === SCHEDULING_KEYS.appointmentSlot
      : currentSchedulingPage === SCHEDULING_KEYS.confirmAppointment

    if (isConfirmButtonDisplayed) {
      return complianceLinks
    }

    return <></>
  }

  return (
    <div className={styles.customerSchedulePage}>
      <div className={styles.flowContainer}>
        <ToastNotification
          severity={alertStatus.severity}
          open={alertStatus.isOpen}
          message={alertStatus.message}
          handleClose={() => toggleAlert(false)}
        />
        <div className={`${styles.customerSchedulePageTitle} font--bold`}>
          <Logo />
          <div className={styles.titleAndServiceDropDownContainer}>
            {TITLES[currentSchedulingPage]}
            {currentSchedulingPage === SCHEDULING_KEYS.chooseAService && (
              <>
                <ServiceDropDown
                  mode={MODES.CUSTOMER}
                  defaultValue={schedulerState.service}
                  onServiceChange={(newValue: string) => {
                    setSchedulerState((prevState: ISchedulerStateProps) => ({
                      ...prevState,
                      service: newValue,
                    }))
                  }}
                />
              </>
            )}
          </div>
        </div>
        <div className={styles.pageContainer}>{renderComponent()}</div>
        <div className={styles.complianceAndButtonContainer}>
          <div className={styles.complianceLinks}>
            {renderComplianceLinks()}
          </div>
          <div className={styles.buttonContainer}>{renderButtons()}</div>
        </div>
      </div>
    </div>
  )
}
